<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{ subjectId: string }>();
const kind = computed(() => {
  if (props.subjectId === 'advanced-math-1')
    return 'surface';
  if (props.subjectId === 'advanced-math-2')
    return 'contour';
  if (props.subjectId.startsWith('linear-algebra'))
    return 'space';
  if (props.subjectId === 'probability-bilingual')
    return 'walk';
  if (props.subjectId === 'number-theory')
    return 'spiral';
  if (props.subjectId === 'statistics')
    return 'scatter';
  return 'bell';
});
const surfaceLines = computed(() => Array.from({ length: 22 }, (_, i) => {
  const fixed = (i % 11 - 5) / 2.5;
  return Array.from({ length: 41 }, (_, j) => {
    const variable = (j - 20) / 10;
    const x = i < 11 ? fixed : variable;
    const y = i < 11 ? variable : fixed;
    const z = Math.exp(-(x * x + y * y) * 0.8);
    return `${100 + x * 23 + y * 20},${112 + x * 10 - y * 10 - z * 72}`;
  }).join(' ');
}));
const walks = Array.from({ length: 4 }, (_, line) => Array.from({ length: 51 }, (_, i) => `${12 + i * 3.5},${128 - i * (1.2 + line * 0.16) + Math.sin(i * 1.7 + line) * 6 + Math.sin(i * 0.45 + line * 2) * 15}`).join(' '));
const dots = Array.from({ length: 76 }, (_, i) => {
  const angle = i * 0.49;
  const radius = 5 + i * 0.88;
  return { x: 103 + Math.cos(angle) * radius, y: 80 + Math.sin(angle) * radius * 0.77 };
});
</script>

<template>
  <svg viewBox="0 0 210 165" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g stroke="currentColor" stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round">
      <g v-if="kind === 'surface'">
        <path d="M22 132 106 161 194 123 M43 140V40 M40 44 43 39 46 44" />
        <polyline v-for="(points, i) in surfaceLines" :key="i" :points="points" opacity="0.5" />
        <path d="m22 132 5-1m-5 1 3 4" /><text x="39" y="33">z</text><text x="13" y="143">x</text><text x="191" y="137">y</text>
      </g>
      <g v-else-if="kind === 'contour'">
        <path d="M15 101H192 M105 149V19 m-3 5 3-5 3 5 m-121 77 5 0 m185 0-5-3" />
        <ellipse v-for="i in 6" :key="i" cx="103" cy="89" :rx="i * 15" :ry="i * 7" transform="rotate(-24 103 89)" opacity="0.72" />
        <text x="109" y="24">y</text><text x="194" y="105">x</text>
      </g>
      <g v-else-if="kind === 'space'">
        <path d="M42 73 113 41 166 67 94 102Z" fill="currentColor" fill-opacity="0.13" stroke-opacity="0.3" />
        <path d="M94 102 166 67V128L94 159Z" fill="currentColor" fill-opacity="0.16" stroke-opacity="0.3" />
        <path d="M42 73 94 102V159L42 129Z" fill="currentColor" fill-opacity="0.07" stroke-opacity="0.3" />
        <path d="M111 19V148 M111 99 24 139 M111 99 190 128 m-81-104 2-5 2 5 m-89 114 5-1-2-4 m161-6 2 5-6 0" />
        <text x="106" y="14">z</text><text x="21" y="150">x</text><text x="193" y="130">y</text>
      </g>
      <g v-else-if="kind === 'walk'">
        <polyline v-for="(points, i) in walks" :key="i" :points="points" :opacity="0.45 + i * 0.13" />
      </g>
      <g v-else-if="kind === 'spiral'">
        <path d="M103 14V150 M29 85H177" opacity="0.4" />
        <circle v-for="(dot, i) in dots" :key="i" :cx="dot.x" :cy="dot.y" r="1.2" fill="currentColor" stroke="none" />
      </g>
      <g v-else-if="kind === 'scatter'">
        <path d="M29 27V146H196 M14 131 191 40 M14 116 192 65" />
        <circle v-for="i in 45" :key="i" :cx="25 + (i * 37 % 165)" :cy="125 - (i * 37 % 165) * 0.46 + Math.sin(i * 7) * 24" r="1.7" fill="currentColor" stroke="none" />
      </g>
      <g v-else>
        <path d="M9 136H201 M107 24V148" opacity="0.65" />
        <path d="M19 134C42 134 60 125 73 99S91 39 107 39 126 77 139 103 168 133 194 134" fill="currentColor" fill-opacity="0.08" />
        <path d="M107 28V145" stroke-dasharray="4 4" /><path d="m105 29 2-5 2 5" />
      </g>
    </g>
  </svg>
</template>

<style scoped>
svg {
  color: #7898d1;
}
text {
  fill: currentColor;
  stroke: none;
  font-size: 11px;
  font-family: Georgia, serif;
}
</style>
